import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../design-system';
import { ChannelSelector } from './ChannelSelector';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface ChannelBindingModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (channels: string[]) => Promise<void>;
  availableChannels: Array<{
    channel: string;
    name: string;
    isConnected: boolean;
  }>;
  currentBindings?: Array<{
    channel: string;
    syncStatus: 'pending' | 'synced' | 'error' | 'never_synced' | 'syncing';
  }>;
  catalogueName?: string;
  loading?: boolean;
}

export const ChannelBindingModal: React.FC<ChannelBindingModalProps> = ({
  visible,
  onClose,
  onConfirm,
  availableChannels,
  currentBindings = [],
  catalogueName,
  loading = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      // Initialize with currently bound channels
      const boundChannels = currentBindings.map(b => b.channel);
      setSelectedChannels(boundChannels);
    } else {
      setSelectedChannels([]);
    }
  }, [visible, currentBindings]);

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await onConfirm(selectedChannels);
      onClose();
    } catch (error) {
      console.error('Error binding channels:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const connectedChannels = availableChannels.filter(ch => ch.isConnected);
  const canSelect = connectedChannels.length > 0;

  const renderContent = () => (
    <>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={colors.textDark} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {catalogueName ? `Bind "${catalogueName}" to Channels` : 'Bind Catalogue to Channels'}
          </Text>
        </View>
      </View>

      {/* Content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading channels...</Text>
        </View>
      ) : (
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
        >
          {!canSelect && (
            <View style={styles.warningContainer}>
              <Ionicons name="warning" size={20} color={colors.warning || '#F59E0B'} />
              <Text style={styles.warningText}>
                No channels are connected. Please connect at least one channel first.
              </Text>
            </View>
          )}

          {canSelect && (
            <>
              <Text style={styles.description}>
                Select one or more channels to bind this catalogue to. You can manage sync settings after binding.
              </Text>
              
              <View style={styles.selectorContainer}>
                <ChannelSelector
                  channels={availableChannels}
                  selectedChannels={selectedChannels}
                  onSelectionChange={setSelectedChannels}
                  bindings={currentBindings}
                  multiSelect={true}
                />
              </View>
            </>
          )}
        </ScrollView>
      )}

      {/* Footer Actions */}
      <View style={styles.footerActions}>
        <Button
          title="Cancel"
          onPress={onClose}
          variant="outline"
          disabled={submitting}
          style={styles.cancelButton}
        />
        <Button
          title={selectedChannels.length > 0 
            ? `Bind ${selectedChannels.length} Channel${selectedChannels.length > 1 ? 's' : ''}`
            : 'Bind Channels'}
          onPress={handleConfirm}
          variant="primary"
          disabled={submitting || !canSelect || selectedChannels.length === 0}
          loading={submitting}
          style={styles.confirmButton}
        />
      </View>
    </>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      transparent={Platform.OS === 'web'}
    >
      {Platform.OS === 'web' ? (
        <View style={styles.webOverlay}>
          <TouchableOpacity
            style={styles.webBackdrop}
            activeOpacity={1}
            onPress={onClose}
          />
          <View style={styles.containerConstrained}>
            {renderContent()}
          </View>
        </View>
      ) : (
        <View style={styles.container}>
          {renderContent()}
        </View>
      )}
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  webOverlay: {
    flex: 1,
    position: 'relative' as any,
  },
  webBackdrop: {
    position: 'absolute' as any,
    top: 180, // Header height
    left: 355, // Left pane width
    right: 355, // Right pane width
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1,
  },
  containerConstrained: {
    position: 'absolute' as any,
    top: 180, // Header height (matches webContentArea paddingTop)
    left: 355, // Left pane width
    right: 355, // Right pane width
    bottom: 0,
    backgroundColor: colors.background.surface,
    zIndex: 2,
    borderRadius: designTokens.borderRadius.lg,
    margin: spacing.lg,
    overflow: 'hidden',
    ...designTokens.shadows.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.25),
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      top: 0,
      backgroundColor: colors.background.surface,
      zIndex: 10,
    }),
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  closeButton: {
    padding: spacing.xs,
    marginRight: spacing.sm,
  },
  headerTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: spacing.md,
  },
  loadingContainer: {
    flex: 1,
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    backgroundColor: (colors.warning || '#F59E0B') + '20',
    borderRadius: designTokens.borderRadius.md,
    marginBottom: spacing.md,
  },
  warningText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
    lineHeight: 20,
  },
  description: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginBottom: spacing.md,
    lineHeight: 20,
  },
  selectorContainer: {
    minHeight: 200,
  },
  footerActions: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
    borderTopWidth: 1,
    borderTopColor: withAlpha(colors.border, 0.25),
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      bottom: 0,
    }),
  },
  cancelButton: {
    flex: 1,
  },
  confirmButton: {
    flex: 1,
  },
});

